import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router";
import { backendGet } from "@accredo/fe-shared/services/dataService.js";
import { useSnackbar } from "notistack";
import { useUser } from "@accredo/fe-shared/hooks/user.js";
import { getUserRowsPerPage } from "../../../utils/userSettings.js";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Typography,
  Box,
} from "@mui/material";
import Spinner from "@accredo/fe-shared/components/Spinner.jsx";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import BookingExportInfoModal from "../../bookings/components/BookingExportInfoModal.jsx";
import PaymentImportStatusBadge from "components/PaymentImportStatusBadge.jsx";
import PaymentImportPaymentsTable from "components/tables/PaymentImportPaymentsTable.jsx";

const PaymentImportShowPage = () => {
  const { setBreadcrumbs } = useBreadcrumb();
  const navigate = useNavigate();
  const { id } = useParams(); // ID des PaymentImports aus der URL
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentImport, setPaymentImport] = useState(null); // file_name speichern

  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUser();
  const default_rows_per_page = getUserRowsPerPage(user, 100);

  const fetchPaymentImport = useCallback(async () => {
    setLoading(true);
    try {
      // const paymentsPromise = backendGet(
      //   `data/payment-imports/${id}/payments`,
      //   {
      //     label: "Payments",
      //     enqueueSnackbar,
      //     setResult: (res) => setPayments(res.data),
      //   }
      // );

      const paymentImportPromise = backendGet(`data/payment-imports/${id}`, {
        label: "PaymentImport",
        enqueueSnackbar,
        setResult: (res) => setPaymentImport(res.data),
      });

      await Promise.all([paymentImportPromise]);
    } catch (err) {
      setError("Fehler beim Laden der Daten.");
    } finally {
      setLoading(false);
    }
  }, [id, enqueueSnackbar]);

  useEffect(() => {
    fetchPaymentImport();
    console.log("fetching data for payment import payments");
  }, []);

  useEffect(() => {
    setBreadcrumbs([
      { name: "Home", color: "secondary", path: "/home" },
      {
        name: "Buchungsverwaltung",
        color: "primary",
        path: "/admin/buchungen",
      },
      {
        name: "Zahlungsimporte",
        color: "primary",
        path: "/admin/buchungen/imports",
      },
      {
        name: `${paymentImport?.file_name}`,
        color: "primary",
        path: `/admin/buchungen/imports/${id}`,
      },
    ]);
  }, [setBreadcrumbs, paymentImport?.id]);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [tableReloadKey, setTableReloadKey] = useState(0);

  const reloadPayments = useCallback(() => {
    fetchPaymentImport();
    setTableReloadKey((key) => key + 1);
  }, [fetchPaymentImport]);

  const bookingExportInfo = useBookingExportInfo();
  const canViewBookingExport = userHasPermission(user, "booking_export.view");

  const handleRowClick = (payment) => {
    setSelectedPayment(payment);
    setModalOpen(true);
  };

  const handleAssignSchadenClick = () => {
    setModalOpen(false);
    navigate(`/admin/buchungen/imports/${id}/payment-matching`);
  };

  if (loading) return <Spinner />;
  if (error) return <p>{error}</p>;

  return (
    <>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h1" gutterBottom>
          Importdetails: {paymentImport?.file_name}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Details und Zahlungen des Importvorgangs
        </Typography>
      </Box>

      <PaymentImportStats paymentImport={paymentImport} />

      <PaymentImportPaymentsTable
        paymentImportId={id}
        defaultRowsPerPage={default_rows_per_page}
        onRowClick={handleRowClick}
        reloadKey={tableReloadKey}
        onReload={reloadPayments}
      />

      <BookingExportInfoModal
        open={bookingExportInfo.open}
        onClose={bookingExportInfo.close}
        bookingExport={bookingExportInfo.bookingExport}
        isLoading={bookingExportInfo.isLoading}
      />

      {/* Modal für Zahlungsdetails */}
      <PaymentModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        payment={selectedPayment}
        onReload={reloadPayments}
        onAssignSchaden={handleAssignSchadenClick}
        onShowExport={
          canViewBookingExport
            ? (bookingExportId) => {
                setModalOpen(false);
                bookingExportInfo.openBookingExport(bookingExportId);
              }
            : undefined
        }
      />
    </>
  );
};

import { useBreadcrumb } from "@accredo/fe-shared/contexts/BreadcrumbContext.jsx";
import {
  formatDatePeriod,
  getDatumZeit,
} from "@accredo/fe-shared/services/formatService.js";
import { useBookingExportInfo } from "../../../hooks/useBookingExportInfo.jsx";
import { userHasPermission } from "@accredo/fe-shared/utils/policyUtils.js";
import { usePaymentIgnoreActions } from "../../../hooks/usePaymentIgnoreActions.jsx";

export const PaymentImportStats = ({ paymentImport }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("de-DE", {
      dateStyle: "short",
      timeStyle: "short",
    });
  };

  return (
    <div style={{ display: "flex", gap: 32, margin: "24px 0" }}>
      <div>
        <strong>Status:</strong>
        <br />
        <PaymentImportStatusBadge status={paymentImport?.status} />
      </div>
      <div>
        <strong>Zeitraum:</strong>
        <br />
        {formatDatePeriod(paymentImport?.from_date, paymentImport?.to_date)}
      </div>
      <div>
        <strong>Gesamt:</strong>
        <br />
        {paymentImport?.total_records ?? 0}
      </div>
      <div>
        <strong>Zugeordnet:</strong>
        <br />
        {paymentImport?.assigned_records ?? 0}
      </div>
      <div>
        <strong>Fehlerhaft:</strong>
        <br />
        {paymentImport?.failed_records ?? 0}
      </div>
      <div>
        <strong>Importiert am:</strong>
        <br />
        {formatDate(paymentImport?.created_at)}
      </div>
    </div>
  );
};

const ActionRow = ({ button, description }) => (
  <Box
    sx={{
      display: "grid",
      gridColumn: "1 / -1",
      gridTemplateColumns: "subgrid",
      alignItems: "center",
      mb: 1.5,
      p: 1.5,
      border: "1px solid #e0e0e0",
      borderRadius: "8px",
      backgroundColor: "#fafafa",
    }}
  >
    <Box
      sx={{
        gridColumn: "1",
        justifySelf: "start",
        minWidth: "180px",
      }}
    >
      {React.cloneElement(button, {
        style: {
          width: "100%",
        },
      })}
    </Box>
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ gridColumn: "2", justifySelf: "start", textAlign: "left" }}
    >
      {description}
    </Typography>
  </Box>
);

export const PaymentModal = ({
  open,
  onClose,
  payment,
  onReload,
  onAssignSchaden,
  onShowExport,
}) => {
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { ignorePayment, restorePayments } = usePaymentIgnoreActions();

  useEffect(() => {
    const fetchBooking = async (bookingId) => {
      setLoading(true);
      await backendGet(`data/bookings/${bookingId}`, {
        label: "Buchung",
        enqueueSnackbar,
        setResult: setBooking,
      });
      setLoading(false);
    };

    const bookingId =
      payment?.booking_id || payment?.error?.existing_payment?.booking_id;

    if (open && bookingId) {
      fetchBooking(bookingId);
    } else {
      setLoading(false);
      setBooking(null);
    }
  }, [open, payment, enqueueSnackbar]);

  const handleIgnore = async () => {
    setIsSubmitting(true);
    const ignored = await ignorePayment(payment.id, { onDone: onReload });
    setIsSubmitting(false);
    if (ignored) onClose();
  };

  const handleRestore = async () => {
    setIsSubmitting(true);
    const response = await restorePayments([payment.id], { onDone: onReload });
    setIsSubmitting(false);
    if (response) onClose();
  };

  const handleGoToClaim = () => {
    if (booking?.schaden_id) {
      navigate(`/schaden/${booking.schaden_id}`);
      onClose();
    }
  };

  if (!payment) {
    return null;
  }

  const isIgnored = Boolean(payment.ignored_at);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Zahlungsdetails</DialogTitle>

      <DialogContent>
        {loading ? (
          <CircularProgress />
        ) : (
          payment && (
            <div>
              <div>
                <strong>Sender:</strong> {payment.remitter}
              </div>
              <div>
                <strong>Betrag:</strong> {payment.amount}
              </div>
              <div>
                <strong>IBAN:</strong> {payment.iban}
              </div>
              <div>
                <strong>BIC:</strong> {payment.bic}
              </div>
              <div>
                <strong>Beschreibung:</strong> {payment.description}
              </div>

              {payment.error ? (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <AlertTitle>{payment.error?.message}</AlertTitle>
                  {payment.error?.existing_payment && (
                    <p>Wurde bereits zu einem Schaden zugeordnet</p>
                  )}
                </Alert>
              ) : null}

              <Box
                sx={{
                  mt: 4,
                  display: "grid",
                  gridTemplateColumns: "auto 1fr",
                  columnGap: "20px",
                }}
              >
                {payment?.status === "assigned" && payment.booking_id && (
                  <ActionRow
                    button={
                      <Button
                        onClick={handleGoToClaim}
                        color="primary"
                        variant="contained"
                      >
                        Schaden anzeigen
                      </Button>
                    }
                    description="Navigiert zur Detailansicht des zugeordneten Schadens."
                  />
                )}
                {payment?.status === "assigned" &&
                  payment.booking_export_id &&
                  onShowExport && (
                    <ActionRow
                      button={
                        <Button
                          onClick={() => onShowExport(payment.booking_export_id)}
                          color="primary"
                          variant="contained"
                        >
                          Export anzeigen
                        </Button>
                      }
                      description="Zeigt den Buchungs-Export an, in dem diese Zahlung enthalten ist."
                    />
                  )}
                {isIgnored && (
                  <>
                    <Alert severity="info" sx={{ gridColumn: "1 / -1" }}>
                      Als nicht relevant markiert
                      {payment.ignored_by?.name
                        ? ` von ${payment.ignored_by.name}`
                        : ""}{" "}
                      am {getDatumZeit(payment.ignored_at)}
                    </Alert>
                    {payment.can_restore && (
                      <ActionRow
                        button={
                          <Button
                            onClick={handleRestore}
                            color="primary"
                            disabled={isSubmitting}
                          >
                            Wiederherstellen
                          </Button>
                        }
                        description="Stellt die Zahlung wieder her. Sie erscheint wieder in der Zahlungszuordnung."
                      />
                    )}
                  </>
                )}
                {!isIgnored && payment?.status === "imported" && (
                  <>
                    <ActionRow
                      button={
                        <Button onClick={onAssignSchaden} color="primary">
                          Zahlung zuordnen
                        </Button>
                      }
                      description="Startet den Prozess, um diese und weitere Zahlungen einem Schaden zuzuordnen."
                    />
                    {payment.can_ignore && (
                      <ActionRow
                        button={
                          <Button
                            onClick={handleIgnore}
                            color="error"
                            disabled={isSubmitting}
                          >
                            Als nicht relevant markieren
                          </Button>
                        }
                        description="Markiert die Zahlung als nicht relevant für die Schadenbearbeitung. Sie kann im Tab „Nicht relevant“ wiederhergestellt werden."
                      />
                    )}
                  </>
                )}
              </Box>
            </div>
          )
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={isSubmitting}>
          Schließen
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentImportShowPage;
